import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute, PublicOnlyRoute } from '@/auth/ProtectedRoute';
import { useAuth } from '@/auth/AuthContext';
import { FullPageSpinner } from '@/components/Skeleton';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { LoginPage } from '@/pages/LoginPage';
import { MicrosoftCallbackPage } from '@/pages/MicrosoftCallbackPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { QuizPage } from '@/pages/QuizPage';
import { ResultsPage } from '@/pages/ResultsPage';
import { ForgotPasswordPage, SetPasswordPage } from '@/pages/SetPasswordPage';

/*
 * The dashboard and history are lazy because they pull in Recharts. Login, the quiz
 * and the results screen stay eager: they are the daily path and must not wait on a
 * second network round trip.
 */
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const HistoryPage = lazy(() =>
  import('@/pages/HistoryPage').then((m) => ({ default: m.HistoryPage })),
);

/*
 * The whole admin console is lazy. Most people signing in are participants and will
 * never load any of it, so it has no business sitting in their bundle.
 */
const AdminOverviewPage = lazy(() =>
  import('@/pages/admin/AdminOverviewPage').then((m) => ({ default: m.AdminOverviewPage })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminImportPage = lazy(() =>
  import('@/pages/admin/AdminImportPage').then((m) => ({ default: m.AdminImportPage })),
);
const AdminQuestionsPage = lazy(() =>
  import('@/pages/admin/AdminQuestionsPage').then((m) => ({ default: m.AdminQuestionsPage })),
);
const AdminQuizzesPage = lazy(() =>
  import('@/pages/admin/AdminQuizzesPage').then((m) => ({ default: m.AdminQuizzesPage })),
);

export function App() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <AppRoutes />
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* ── public ── */}
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/invite/accept" element={<SetPasswordPage mode="invite" />} />
        <Route path="/reset-password" element={<SetPasswordPage mode="reset" />} />
      </Route>

      {/*
        The SSO callback is deliberately outside PublicOnlyRoute: it must run its
        exchange even though it will end up authenticated, and a redirect guard would
        bounce it before the code is redeemed.
      */}
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallbackPage />} />

      {/* ── participant ── */}
      <Route element={<ProtectedRoute />}>
        {/* Must sit inside the guard (it needs a session) and is the one route the
            mustChangePassword redirect allows through. */}
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/quiz" element={<QuizPage />} />
        <Route path="/results/:attemptId" element={<ResultsPage />} />
        <Route path="/history" element={<HistoryPage />} />
      </Route>

      {/* ── admin console ── */}
      <Route element={<ProtectedRoute roles={['PROGRAM_ADMIN']} />}>
        <Route path="/admin/overview" element={<AdminOverviewPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/import" element={<AdminImportPage />} />
        <Route path="/admin/questions" element={<AdminQuestionsPage />} />
        <Route path="/admin/quizzes" element={<AdminQuizzesPage />} />
        {/* Any other /admin path lands on the console's own home, not a 404. */}
        <Route path="/admin/*" element={<Navigate to="/admin/overview" replace />} />
      </Route>

      <Route path="/" element={<LandingRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/** §2 — the destination comes from the server, never from a client-side role check. */
function LandingRedirect() {
  const { me, loading } = useAuth();

  if (loading) return <FullPageSpinner />;
  return <Navigate to={me ? me.landingPath : '/login'} replace />;
}

